import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Button, Skeleton, TablePagination } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Dayjs from 'dayjs';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import MockupHistoryFilters, { PREVIEW_PAGE_SIZE } from '../../components/MockupHistoryFilters';
import MockupViewControls from '../../components/MockupViewControls';
import MockupsCsvExport from '../../components/MockupsCsvExport';
import MockupEventCard from '../../components/MockupEventCard';
import MockupEventDialog from '../../components/MockupEventDialog';
import CustomerHistoryDialog from '../../components/CustomerHistoryDialog';
import { Mockups } from '../../store/slices';
import { IStore } from '../../store/types';
import { MockupEvent, asFlag, mockupEventId, safeHref } from '../../lib/mockupEvents';
import { useStyles } from './styles';

export interface MockupPreviewPageProps {
    title: string;
    subtitle: string;
    /** Coloring (3) and Blanket (5) pin a generation type; the main preview sends none. */
    generationTypeId?: number;
    /** Only the main preview can group the page by customer. */
    allowGrouping?: boolean;
}

interface Group {
    key: string;
    customerId: string | number | null;
    events: MockupEvent[];
}

const createRequestId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const today = () => Dayjs(new Date()).format('YYYY-MM-DD');

const dropRequestId = (params: any) => {
    if (!params) return {};
    const { requestId, ...rest } = params;
    return rest;
};

/**
 * Shared screen of the three preview grids (`/mockups-image`, `/mockups-coloring`, `/mockups-towel`).
 * They share `state.mockups`, so every request strips the other previews' generation type and applies its own.
 */
const MockupPreviewPage = ({ title, subtitle, generationTypeId, allowGrouping = false }: MockupPreviewPageProps) => {
    const { mockups, status, queryParams } = useSelector<IStore, { mockups: any; status: string; queryParams: any }>(
        (state) => ({ mockups: state.mockups.data, status: state.mockups.status, queryParams: state.mockups.queryParams }),
        shallowEqual
    );
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [columns, setColumns] = useState(4);
    const [showTags, setShowTags] = useState(false);
    const [groupByCustomer, setGroupByCustomer] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [infoEvent, setInfoEvent] = useState<MockupEvent | null>(null);
    const [validResults, setValidResults] = useState<any>(null);
    const lastRequestIdRef = useRef<string>('');
    /** The last params sent, for Retry: a rejected request never reaches the store's queryParams. */
    const lastParamsRef = useRef<any>(null);
    const classes = useStyles({ columns });
    const historyCustomerId = searchParams.get('customerId');

    const defaultParams = useMemo(
        () => ({ limit: PREVIEW_PAGE_SIZE, page: 1, total: 0, minDate: today(), maxDate: today(), ...(generationTypeId ? { generationTypeId } : {}) }),
        [generationTypeId]
    );

    const loadMockups = (params: any = {}) => {
        const requestId = params?.requestId ?? createRequestId();
        const { generationTypeId: pinned, generationType, defaultTypeID, ...rest } = params || {};
        const normalized = { ...rest, requestId, ...(generationTypeId ? { generationTypeId } : {}) };
        lastRequestIdRef.current = requestId;
        lastParamsRef.current = normalized;
        dispatch<any>(Mockups.getMockups(normalized));
    };
    const retry = () => loadMockups(dropRequestId(lastParamsRef.current));

    // Only results of the latest request are shown; older responses that arrive late are ignored.
    useEffect(() => {
        if (status === 'loading') return;
        if (mockups && queryParams?.requestId) {
            if (queryParams.requestId === lastRequestIdRef.current) setValidResults(mockups);
        } else if (mockups && !queryParams?.requestId) {
            setValidResults(mockups);
        }
    }, [mockups, queryParams?.requestId, status]);

    useEffect(() => {
        loadMockups({
            ...dropRequestId(queryParams),
            limit: queryParams?.limit || PREVIEW_PAGE_SIZE,
            minDate: queryParams?.minDate || defaultParams.minDate,
            maxDate: queryParams?.maxDate || defaultParams.maxDate,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- first load only; the filters drive every later request
    }, []);

    useEffect(() => {
        setExpandedGroups(new Set());
    }, [validResults, groupByCustomer]);

    const results = useMemo<MockupEvent[]>(() => (Array.isArray(validResults) ? validResults : []), [validResults]);

    // Group the loaded page by customerId, keeping the server order (first appearance wins).
    const groups = useMemo<Group[]>(() => {
        if (!groupByCustomer) return [];
        const map = new Map<string, Group>();
        results.forEach((event, index) => {
            // Events without a customer are never grouped together: each stays its own tile.
            const key = event?.customerId != null ? String(event.customerId) : `unknown-${index}`;
            if (!map.has(key)) map.set(key, { key, customerId: event?.customerId ?? null, events: [] });
            map.get(key)!.events.push(event);
        });
        return Array.from(map.values());
    }, [groupByCustomer, results]);

    const toggleGroup = (key: string) =>
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    // Both with `replace`: the dialog must not leave a duplicate history entry behind.
    const openHistory = useCallback((customerId: string | number) => setSearchParams({ customerId: String(customerId) }, { replace: true }), [setSearchParams]);
    const closeHistory = () => setSearchParams({}, { replace: true });
    const openInfo = useCallback((event: MockupEvent) => setInfoEvent(event), []);

    const handleChangePage = (_: unknown, newPage: number) => {
        loadMockups({ ...dropRequestId(queryParams), page: newPage + 1 });
    };

    const loading = status === 'loading';
    const failed = status === 'rejected';
    const total = Number(queryParams?.total) || 0;
    const page = Number(queryParams?.page) || 1;
    const limit = Number(queryParams?.limit) || PREVIEW_PAGE_SIZE;
    const mergedParams = { ...defaultParams, ...queryParams };
    const range = { minDate: mergedParams.minDate, maxDate: mergedParams.maxDate };

    const renderCard = (event: MockupEvent, key: string) => (
        <MockupEventCard key={key} event={event} showTags={showTags} onInfo={openInfo} onHistory={openHistory} />
    );

    const renderGroup = (group: Group) => {
        if (group.events.length === 1) return renderCard(group.events[0], `g-${group.key}`);
        const expanded = expandedGroups.has(group.key);
        const cover = safeHref(group.events[0]?.mockupUrl);
        const inCart = group.events.filter((event) => asFlag(event.toCart)).length;
        const ordered = group.events.filter((event) => asFlag(event.orderExist)).length;
        const label = group.customerId != null ? `Customer ${group.customerId}` : 'No customer';
        if (!expanded) {
            return (
                <button
                    type="button"
                    key={group.key}
                    className={classes.stackTile}
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={false}
                    aria-label={`${label}: ${group.events.length} uploads, expand`}
                >
                    <div className={classes.stackMedia}>
                        {cover ? <img loading="lazy" src={cover} alt="" className={classes.stackImage} /> : <div className={classes.stackEmpty}>No mockup</div>}
                        <div className={classes.stackBadges}>
                            <StatusChip tone="brand" size="sm">{group.events.length} uploads</StatusChip>
                            {inCart > 0 && <StatusChip tone="warning" size="sm">{inCart} in cart</StatusChip>}
                            {ordered > 0 && <StatusChip tone="success" size="sm">{ordered} ordered</StatusChip>}
                        </div>
                        <div className={classes.stackFooter}>
                            <span>{label}</span>
                            <span>Expand</span>
                        </div>
                    </div>
                </button>
            );
        }
        return (
            <section key={group.key} className={classes.group} aria-label={label}>
                <div className={classes.groupHeader}>
                    <button type="button" className={classes.groupToggle} onClick={() => toggleGroup(group.key)} aria-expanded={true}>
                        <strong>{label}</strong>
                        <span className={classes.groupCount}>
                            {group.events.length} uploads
                            {inCart > 0 ? ` · ${inCart} in cart` : ''}
                            {ordered > 0 ? ` · ${ordered} ordered` : ''}
                        </span>
                        <ExpandLessIcon fontSize="small" aria-hidden="true" />
                    </button>
                    {group.customerId != null && (
                        <div className={classes.groupActions}>
                            <Button size="small" variant="outlined" onClick={() => openHistory(group.customerId as string | number)}>History</Button>
                        </div>
                    )}
                </div>
                <div className={classes.groupGrid}>{group.events.map((event, index) => renderCard(event, `g-${group.key}-${mockupEventId(event) ?? index}`))}</div>
            </section>
        );
    };

    let body: React.ReactNode;
    if (loading || (validResults === null && !failed)) {
        body = (
            <div className={classes.grid} aria-busy="true" aria-label="Loading uploads">
                {Array.from({ length: Math.max(columns * 2, 4) }).map((_, index) => (
                    <Skeleton key={index} variant="rounded" height="auto" className={classes.skeleton} />
                ))}
            </div>
        );
    } else if (failed) {
        body = (
            <EmptyState
                title="The uploads could not be loaded"
                hint="Check the connection and try again."
                action={<Button variant="outlined" onClick={retry}>Retry</Button>}
            />
        );
    } else if (results.length === 0) {
        body = <EmptyState title="No uploads for these filters" hint="Widen the date range or reset the filters." />;
    } else {
        body = (
            <div className={classes.grid}>
                {groupByCustomer ? groups.map(renderGroup) : results.map((event, index) => renderCard(event, `${mockupEventId(event) ?? 'event'}-${index}`))}
            </div>
        );
    }

    return (
        <PageContainer constrained>
            <PageHeader eyebrow="Mockups" title={title} subtitle={subtitle} actions={<MockupsCsvExport queryParams={mergedParams} loading={loading} />} />
            <Panel
                padding="none"
                title="Uploads"
                badges={!loading && !failed && validResults ? <StatusChip tone="neutral">{total.toLocaleString('en-US')} total</StatusChip> : undefined}
                actions={(
                    <MockupViewControls
                        columns={columns}
                        onColumns={setColumns}
                        showTags={showTags}
                        onShowTags={setShowTags}
                        groupByCustomer={allowGrouping ? groupByCustomer : undefined}
                        onGroupByCustomer={allowGrouping ? setGroupByCustomer : undefined}
                    />
                )}
                footer={
                    !loading && !failed && (results.length > 0 || page > 1) ? (
                        <TablePagination
                            className={classes.pagination}
                            rowsPerPageOptions={[]}
                            component="div"
                            count={total || results.length}
                            rowsPerPage={limit}
                            page={Math.max(0, page - 1)}
                            onPageChange={handleChangePage}
                        />
                    ) : undefined
                }
            >
                <MockupHistoryFilters queryParams={mergedParams} loadMockups={loadMockups} loading={loading} defaultTypeID={generationTypeId} />
                <div className={classes.scroller}>{body}</div>
            </Panel>
            <MockupEventDialog
                event={infoEvent}
                open={!!infoEvent}
                onClose={() => setInfoEvent(null)}
                onHistory={(customerId) => {
                    setInfoEvent(null);
                    openHistory(customerId);
                }}
            />
            <CustomerHistoryDialog open={!!historyCustomerId} customerId={historyCustomerId} onClose={closeHistory} range={range} />
        </PageContainer>
    );
};

export default MockupPreviewPage;
